import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, screenSizeSmaller} from 'views/components';

import ClientFeed from './client_scenes/client_feed/ClientFeed';
import {readLocalStorage, writeLocalStorage} from 'utils/storage/local_storage';

import _ from 'lodash';
import moment from 'moment';
import backend from 'utils/backend';

const ClientView = (props) => {
  const [refresh, setRefresh] = React.useState(1);

  const [clientData, setClientData] = React.useState();
  const [clientMessages, setClientMessage] = React.useState({});
  const [clientLogs, setClientLogs] = React.useState({});
  const [clientScheduleEvents, setClientScheduleEvents] = React.useState({});
  const [clientCareviewSettings, setClientCareviewSettings] = React.useState({});
  const [clientForms, setClientForms] = React.useState([]);
  const [clientPaymentSlipWeekly, setClientPaymentSlipWeekly] = React.useState([]);
  const [clientPaymentSlipMonthly, setClientPaymentSlipMonthly] = React.useState([]);
  const [clientPaymentPlan, setClientPaymentPlan] = React.useState(null);
  const [clientCareplan, setClientCareplan] = React.useState({});
  const [clientAuthentication, setClientAuthentication] = React.useState({});
  const [clientNotificationChannels, setClientNotificationChannels] = React.useState({});
  const [clientCommunication, setClientCommunication] = React.useState({});
  const [clientDialpadIntegration, setClientDialpadIntegration] = React.useState({});
  const [clientTasks, setClientTasks] = React.useState([]);
  const [clientOwnerData, setClientOwnerData] = React.useState({});
  const [clientCallStatus, setClientCallStatus] = React.useState(null);

  const [clientMessagesLastEvaluatedKey, setClientMessagesLastEvaluatedKey] = React.useState(null);
  const [clientLogsLastEvaluatedKey, setClientLogsLastEvaluatedKey] = React.useState(null);

  const [clientDataLoaded, setClientDataLoaded] = React.useState(false);
  const [clientMessagesLoading, setClientMessagesLoading] = React.useState(false);
  const [clientLogsLoading, setClientLogsLoading] = React.useState(false);
  const [clientPaymentSlipLoading, setClientPaymentSlipLoading] = React.useState(false);
  const [clientPaymentPlanLoading, setClientPaymentPlanLoading] = React.useState(false);
  const [ClientAuthenticationLoading, setClientAuthenticationLoading] = React.useState(false);
  const [clientNotificationLoading, setClientNotificationLoading] = React.useState(false);
  const [clientCommunicationLoading, setClientCommunicationLoading] = React.useState(false);
  const [clientDialpadIntegrationLoading, setClientDialpadIntegrationLoading] = React.useState(false);
  const [clientTasksLoading, setClientTasksLoading] = React.useState(false);
  const [clientTasksLoaded, setClientTasksLoaded] = React.useState(false);
  const [clientTasksFilter, setClientTasksFilter] = React.useState('all');

  const [feedTab, setFeedTab] = React.useState('CAREPLAN');

  const progressWrap = props.statusProgressWrap;
  const clientRn = props.clientRn;
  const requester = props.requester;

  const requesterRn = requester.userRn;

  const showSettings = screenSizeSmaller('md');

  React.useEffect(() => {
    clientBackend.load.loadClientData(clientRn);
  }, [clientRn, refresh]);
  React.useEffect(() => {
    clientBackend.load.loadMessages(clientRn, null);
  }, [clientRn, refresh]);
  React.useEffect(() => {
    clientBackend.load.loadLogs(clientRn, null);
  }, [clientRn, refresh]);
  React.useEffect(() => {
    setClientTasks([]);
    setClientTasksLoaded(false);
    setClientTasksLoading(false);
  }, [clientRn, refresh]);

  React.useEffect(() => {
    if (clientTasksLoaded || clientTasksLoading) {
      return;
    }

    clientBackend.load.loadTasks(clientRn);
  }, [clientRn, clientTasksLoaded, clientTasksLoading, feedTab]);

  React.useEffect(() => {
    const currentValues = readLocalStorage('CLIENT_MESSAGES_LAST_SEEN');
    const nextValues = {
      ...currentValues,
      [clientRn]: moment().toISOString(),
    };
    writeLocalStorage('CLIENT_MESSAGES_LAST_SEEN', nextValues);
  }, [clientRn]);

  const hydrateTaskRecord = React.useCallback(async (taskData, previousTask=null) => {
    const normalizedOwnerRns = Array.isArray(taskData?.ownerRns) ? taskData.ownerRns :
      Array.isArray(taskData?.ownerRn) ? taskData.ownerRn :
        taskData?.ownerRn ? [taskData.ownerRn] :
          (previousTask?.ownerRns || []);
    const normalizedTask = {
      ...previousTask,
      ...taskData,
      ownerRns: normalizedOwnerRns,
      connections: Array.isArray(taskData?.connections) ? taskData.connections : (previousTask?.connections || []),
      type: taskData?.type || previousTask?.type || null,
    };
    const connectionRns = normalizedTask.connections || [];
    const ownerRns = normalizedTask.ownerRns || [];

    const connectionInfo = await Promise.all(connectionRns.map(async (connectionRn) => {
      if (/^RN:CLIENT-V1-[^:]+$/.test(connectionRn)) {
        try {
          const clientResponse = await backend.client.getClient(connectionRn);
          const clientDetail = clientResponse?.clientDetail;
          const [firstName, ...lastNameParts] = (clientDetail?.clientName || '').trim().split(/\s+/).filter(Boolean);

          return {
            connectionRn,
            firstName,
            lastName: lastNameParts.join(' '),
          };
        } catch {
          return null;
        }
      }

      if (/^RN:CAREGIVER-V1-[^:]+$/.test(connectionRn)) {
        try {
          const caregiverResponse = await backend.caregiver.getCaregiver(connectionRn);
          const caregiverDetail = caregiverResponse?.caregiverDetail;
          const [firstName, ...lastNameParts] = (caregiverDetail?.caregiverName || '').trim().split(/\s+/).filter(Boolean);

          return {
            connectionRn,
            firstName,
            lastName: lastNameParts.join(' '),
          };
        } catch {
          return null;
        }
      }

      return null;
    }));

    const ownerInfo = await Promise.all(ownerRns.map(async (ownerRn) => {
      try {
        const adminResponse = await backend.user.getUser(ownerRn);

        return {
          ownerRn,
          firstName: adminResponse?.user?.firstName,
          lastName: adminResponse?.user?.lastName,
        };
      } catch {
        return null;
      }
    }));

    return {
      ...normalizedTask,
      connectionInfo: connectionInfo.filter(Boolean),
      ownerInfo: ownerInfo.filter(Boolean),
    };
  }, []);

  const clientBackend = {
    load: {
      loadClientData: async (clientRn) => {
        progressWrap(async () => {
          const output = await backend.client.getClient(clientRn, true, true, true, true, true, true);
          setClientData(output.clientDetail);
          setClientCareviewSettings(output.careview);
          setClientForms(output.forms);
          setClientDataLoaded(true);
          setClientCareplan(output.careplan);
          setClientCallStatus(output.callStatus?.introCallStatus);
          setClientOwnerData(output.ownerData);
        }, {debugString: 'CLIENT_LOAD'});
      },
      loadMessages: async (clientRn, clientMessagesLastEvaluatedKey) => {
        progressWrap(async () => {
          setClientMessagesLoading(true);
          const output = await backend.client.message.load(clientRn, clientMessagesLastEvaluatedKey, true, true);

          setClientMessage((messages) => {
            const newMessages = output.messages.reduce((result, message) => {
              return {
                ...result,
                [message.messageRn]: message,
              };
            }, {});

            return {
              ...messages,
              ...newMessages,
            };
          });
          setClientMessagesLastEvaluatedKey(output.lastEvaluatedKey);
          setClientMessagesLoading(false);
        }, {debugString: 'CLIENT_MESSAGE_LOAD'});
      },
      loadLogs: async (clientRn, clientLogsLastEvaluatedKey) => {
        progressWrap(async () => {
          setClientLogsLoading(true);
          const output = await backend.client.loadLogs(clientRn, clientLogsLastEvaluatedKey, true);

          setClientLogs((logs) => {
            const newLogs = output.logs.reduce((result, log) => {
              return {
                ...result,
                [log.clientLogRn]: log,
              };
            }, {});

            return {
              ...logs,
              ...newLogs,
            };
          });
          setClientLogsLastEvaluatedKey(output.lastEvaluatedKey);
          setClientLogsLoading(false);
        }, {debugString: 'CLIENT_LOGS_LOAD'});
      },
      loadTasks: async (clientRn) => {
        progressWrap(async () => {
          setClientTasksLoading(true);

          try {
            const filters = [
              {key: 'resourceRn', value: clientRn},
              {key: 'status', value: ['OPEN', 'COMPLETE']},
            ];
            const sort = {
              sortKey: 'dueDate',
              sortDirection: 'ASC',
            };
            let allUserTasks = [];
            let lastEvaluatedKey = null;

            while (lastEvaluatedKey !== undefined) {
              const userTaskResponse = await backend.task.queryResourceTasks(filters, sort, lastEvaluatedKey);

              allUserTasks = [...allUserTasks, ...(userTaskResponse?.list || [])];
              lastEvaluatedKey = userTaskResponse?.lastEvaluatedKey;

              if (!lastEvaluatedKey) {
                break;
              }
            }

            setClientTasks(allUserTasks ?? []);
            setClientTasksLoaded(true);
          } finally {
            setClientTasksLoading(false);
          }
        }, {debugString: 'TASKS_LOAD'});
      },
    },
    detail: {
      editDescription: async (newBody, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              description: {
                blob: newBody,
                editorType: 'MARKDOWN',
              },
            };
            const {clientDetail, updateLog} = await backend.client.updateDescription(clientRn, body);

            // Refresh
            setClientData(clientDetail);

            setClientLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLogRn]: updateLog,
              };
            });

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_EDIT_DESCRIPTION'});
      },
      editPhoto: async (file, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const type = file.type;
            const name = file.name;
            const body = {
              fileType: type,
              fileName: name,
            };
            const {clientDetail, signedUrl, updateLog} = await backend.client.updateFile(clientRn, body);

            const uploadResult = await backend.s3.upload(signedUrl, file);

            if (uploadResult.status === 200) {
              setClientData(clientDetail);
            } else {
              console.error('Upload failed with status', result.status);
            }

            setClientLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLogRn]: updateLog,
              };
            });

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_EDIT_PHOTO'});
      },
      editStatus: async (status, onSuccess, onError=()=>undefined, message=null) => {
        progressWrap(async () => {
          try {
            const body = {
              status,
            };
            if (message) {
              body.message = message;
            }
            const {clientDetail, updateLog} = await backend.client.updateStatus(clientRn, body);

            // Refresh
            setClientData(clientDetail);

            setClientLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLogRn]: updateLog,
              };
            });

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_EDIT_STATUS'});
      },
      editCallStatus: async (status, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              introCallStatus: status,
            };
            const {callStatus} = await backend.client.editCallStatus(clientRn, body);

            setClientCallStatus(callStatus.introCallStatus);

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_EDIT_CALL_STATUS'});
      },
      editName: async (clientName, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              clientName,
            };
            const {clientDetail, updateLog} = await backend.client.updateName(clientRn, body);

            // Refresh
            setClientData(clientDetail);

            setClientLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLogRn]: updateLog,
              };
            });

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_EDIT_NAME'});
      },
      editClientOwner: async (owner, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              owner: {
                userRn: owner,
              },
            };
            const {clientOwner} = await backend.client.editClientOwner(clientRn, body);

            // Refresh
            setClientOwnerData(clientOwner);

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_EDIT_OWNER'});
      },
      addContact: async (type, detail, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              type,
              detail,
            };
            const {clientDetail, updateLog} = await backend.client.addContact(clientRn, body);

            // Refresh
            setClientData(clientDetail);

            setClientLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLogRn]: updateLog,
              };
            });

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_ADD_CONTACT'});
      },
      removeContact: async (type, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              type,
            };
            const {clientDetail, updateLog} = await backend.client.removeContact(clientRn, body);

            // Refresh
            setClientData(clientDetail);

            setClientLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLogRn]: updateLog,
              };
            });

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_REMOVE_CONTACT'});
      },
      addCaregiver: async (caregiverData, onSuccess, onError=()=>undefined) => {
        const {
          caregiverRn,
          status,
          recommendation,
          languageChips,
          recommendationChips,
          availabilityOverlap,
          availability,
          availabilityLastUpdatedTime,
        } = caregiverData;

        progressWrap(async () => {
          try {
            if (recommendation) {
              const recommendationData = {
                recommendationId: clientRn,
                recommendation: {
                  blob: recommendation,
                  editorType: 'MARKDOWN',
                },
                languageChips,
                recommendationChips,
                availabilityOverlap,
                availability,
                availabilityLastUpdatedTime,
                overwrite: true,
              };
              await backend.caregiver.addRecommendation(caregiverRn, recommendationData);
            }

            const body = {
              caregiverRn,
              status,
            };

            const {clientDetail, updateLog} = await backend.client.addCaregiver(clientRn, body);
            // Refresh
            setClientData(clientDetail);
            setClientLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLogRn]: updateLog,
              };
            });
            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_ADD_CAREGIVER'});
      },
      removeCaregiver: async (caregiverRn, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              caregiverRn,
            };
            const {clientDetail, updateLog} = await backend.client.removeCaregiver(clientRn, body);
            // Refresh
            setClientData(clientDetail);
            setClientLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLogRn]: updateLog,
              };
            });
            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_REMOVE_CAREGIVER'});
      },
      relationship: {
        add: async (relationship, onSuccess, onError=()=>undefined) => {
          progressWrap(async () => {
            try {
              const body = {
                relationship,
              };
              const {clientDetail, updateLog} = await backend.client.relationship.add(clientRn, body);

              // Refresh
              setClientData(clientDetail);

              setClientLogs((logs) => {
                return {
                  ...logs,
                  [updateLog.clientLogRn]: updateLog,
                };
              });

              onSuccess();
            } catch (error) {
              onError();
              console.error(error);
              throw new Error(error);
            }
          }, {debugString: 'CLIENT_ADD_RELATIONSHIP'});
        },
        edit: async (relationshipRn, relationship, onSuccess, onError=()=>undefined) => {
          progressWrap(async () => {
            try {
              const body = {
                relationship,
              };
              const {clientDetail, updateLog} = await backend.client.relationship.edit(clientRn, relationshipRn, body);

              // Refresh
              setClientData(clientDetail);

              setClientLogs((logs) => {
                return {
                  ...logs,
                  [updateLog.clientLogRn]: updateLog,
                };
              });

              onSuccess();
            } catch (error) {
              onError();
              console.error(error);
              throw new Error(error);
            }
          }, {debugString: 'CLIENT_EDIT_RELATIONSHIP'});
        },
        remove: async (relationshipRn, onSuccess, onError=()=>undefined) => {
          progressWrap(async () => {
            try {
              const {clientDetail, updateLog} = await backend.client.relationship.remove(clientRn, relationshipRn);

              // Refresh
              setClientData(clientDetail);

              setClientLogs((logs) => {
                return {
                  ...logs,
                  [updateLog.clientLogRn]: updateLog,
                };
              });

              onSuccess();
            } catch (error) {
              onError();
              console.error(error);
              throw new Error(error);
            }
          }, {debugString: 'CLIENT_REMOVE_RELATIONSHIP'});
        },
      },
    },
    messages: {
      create: async (messageType, newMessage, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              body: {
                blob: newMessage,
                editorType: 'MARKDOWN',
              },
              messageType: messageType,
              requester: {
                rn: requesterRn,
              },
              source: 'WEBSITE',
              sentBy: 'ADMIN',
            };
            const result = await backend.client.message.create(clientRn, body);

            setClientMessage((messages) => {
              return {
                ...messages,
                [result.messageRn]: result.message,
              };
            });

            if (messageType === 'ANNOUNCEMENTS') {
              setClientData((clientData) => {
                return {
                  ...clientData,
                  latestAnnouncementMessageRn: result.messageRn,
                  latestAnnouncementMessage: result.message,
                };
              });
            }
            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_MESSAGE_CREATE'});
      },
      sendEmail: async (relationshipRn, emailData, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const result = await backend.notification.sendEmail(relationshipRn, emailData);
            const messageRn = result?.result?.messageRn;
            const message = result?.result?.message;

            if (messageRn && message) {
              setClientMessage((messages) => {
                return {
                  ...messages,
                  [messageRn]: message,
                };
              });
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_MESSAGE_SEND_EMAIL'});
      },
      sendSms: async (relationshipRn, newMessage, fromEmail, fromName, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            await backend.notification.sendSms(relationshipRn, newMessage, fromEmail);

            const createdAt = new Date().toISOString();
            const senderName = fromName;
            const localMessage = {
              messageRn: `LOCAL-SENT-${createdAt}-${Math.random().toString(36).slice(2, 10)}`,
              messageType: 'DIALPAD_SMS_TEXT',
              body: {
                blob: `${senderName}: ${newMessage}`,
                editorType: 'MARKDOWN',
              },
              source: 'WEBSITE',
              sentBy: 'ADMIN',
              createdAt,
              updatedAt: createdAt,
              submittedBy: {
                type: 'EXTERNAL',
                resourceRn: 'RN:SERVER-V1-DIALPADINTEGRATION',
              },
              emoji: {},
              emojisWithRequesterData: {},
              isLocalOnly: true,
            };

            setClientMessage((messages) => {
              return {
                ...messages,
                [localMessage.messageRn]: localMessage,
              };
            });

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_MESSAGE_SEND_SMS'});
      },
      update: async (messageRn, newMessage, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              body: {
                blob: newMessage,
                editorType: 'MARKDOWN',
              },
            };
            const result = await backend.client.message.update(clientRn, messageRn, body);

            setClientMessage((messages) => {
              messages[result.messageRn] = result.message;
              return {
                ...messages,
              };
            });

            if (clientData.latestAnnouncementMessageRn === result.messageRn) {
              setClientData((clientData) => {
                return {
                  ...clientData,
                  latestAnnouncementMessageRn: result.messageRn,
                  latestAnnouncementMessage: result.message,
                };
              });
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_MESSAGE_UPDATE',
        });
      },
      delete: async (messageRn, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const result = await backend.client.message.delete(clientRn, messageRn);

            setClientMessage((messages) => {
              delete messages[result.messageRn];
              return {
                ...messages,
              };
            });

            if (clientData.latestAnnouncementMessageRn === result.messageRn) {
              setClientData((clientData) => {
                return {
                  ...clientData,
                  latestAnnouncementMessageRn: null,
                  latestAnnouncementMessage: null,
                };
              });
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_MESSAGE_DELETE',
        });
      },
      addEmoji: async (messageRn, emoji, userRn, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              emoji: emoji,
              requester: {
                rn: userRn,
              },
            };
            const result = await backend.client.message.emoji.add(clientRn, messageRn, body);

            setClientMessage((messages) => {
              messages[result.messageRn] = result.message;
              return {
                ...messages,
              };
            });

            if (clientData.latestAnnouncementMessageRn === result.messageRn) {
              setClientData((clientData) => {
                return {
                  ...clientData,
                  latestAnnouncementMessageRn: result.messageRn,
                  latestAnnouncementMessage: result.message,
                };
              });
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_MESSAGE_EMOJI_ADD',
        });
      },
      removeEmoji: async (messageRn, emoji, userRn, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              emoji: emoji,
              requester: {
                rn: userRn,
              },
            };
            const result = await backend.client.message.emoji.remove(clientRn, messageRn, body);

            setClientMessage((messages) => {
              messages[result.messageRn] = result.message;
              return {
                ...messages,
              };
            });

            if (clientData.latestAnnouncementMessageRn === result.messageRn) {
              setClientData((clientData) => {
                return {
                  ...clientData,
                  latestAnnouncementMessageRn: result.messageRn,
                  latestAnnouncementMessage: result.message,
                };
              });
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_MESSAGE_EMOJI_REMOVE',
        });
      },
    },
    schedule: {
      addFixedEvent: async (newEvent, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            backend.caregiver.addClient(newEvent.caregiverData.caregiverRn, {clientRn});
            backend.client.addCaregiver(clientRn, {caregiverRn: newEvent.caregiverData.caregiverRn, status: 'ACTIVE'});

            await backend.client.schedule.addEvent(clientRn, newEvent);

            setClientScheduleEvents({});
            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_SCHEDULE_ADD_EVENT',
        });
      },
      addRecurrence: async (newEvent, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            backend.caregiver.addClient(newEvent.caregiverData.caregiverRn, {clientRn});
            backend.client.addCaregiver(clientRn, {caregiverRn: newEvent.caregiverData.caregiverRn, status: 'ACTIVE'});

            await backend.client.schedule.addRecurrence(clientRn, newEvent);

            setClientScheduleEvents({});
            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_SCHEDULE_ADD_RECURRENCE',
        });
      },
      addLiveIn: async (newEvent, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            backend.caregiver.addClient(newEvent.caregiverData.caregiverRn, {clientRn});
            backend.client.addCaregiver(clientRn, {caregiverRn: newEvent.caregiverData.caregiverRn, status: 'ACTIVE'});

            await backend.client.schedule.addLiveIn(clientRn, newEvent);

            setClientScheduleEvents({});
            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_SCHEDULE_ADD_LIVE_IN',
        });
      },
      deactivateRecurrence: async (recurrenceRn, deactivatedData, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              deactivatedData,
            };
            await backend.client.schedule.deactivateRecurrence(clientRn, recurrenceRn, body);

            setClientScheduleEvents({});

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_SCHEDULE_DEACTIVATE_RECURRENCE',
        });
      },
      deactivateLiveIn: async (liveInRn, deactivatedData, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              deactivatedData,
            };
            await backend.client.schedule.deactivateLiveIn(clientRn, liveInRn, body);

            setClientScheduleEvents({});

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_SCHEDULE_DEACTIVATE_LIVE_IN',
        });
      },
      cancelEvent: async (eventRn, reasonData, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            await backend.event.cancelEvent(clientRn, eventRn, reasonData);

            setClientScheduleEvents({});
            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_SCHEDULE_CANCEL_EXCEPTION_RECURRENCE',
        });
      },
      loadMonthlyEvents: async (byMonths, loadCaregiverUpdates, options={}) => {
        progressWrap(async () => {
          const output = await backend.client.schedule.loadByMonth(clientRn, byMonths, loadCaregiverUpdates, options);

          const events = output.events.reduce((result, event) => {
            return {
              ...result,
              [event.eventRn]: event,
            };
          }, {});

          setClientScheduleEvents((e) => {
            return {
              ...e,
              ...events,
            };
          });
        }, {debugString: 'CLIENT_SCHEDULE_LOAD_EVENTS'});
      },
      rescheduleEvent: async (eventRn, eventData, reasonForException, onSuccess=()=>undefined) => {
        progressWrap(async () => {
          await backend.event.rescheduleEvent(clientRn, eventRn, reasonForException, eventData);

          setClientScheduleEvents({});
          onSuccess();
        }, {
          debugString: 'CLIENT_SCHEDULE_RESCHEDULE_EVENT',
        });
      },
    },
    careview: {
      addQuestion: async (type, questionData, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const {careview} = await backend.client.careview.addQuestion(clientRn, type, questionData);

            setClientCareviewSettings(careview);

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_CAREVIEW_ADD_QUESTION',
        });
      },
      updateQuestion: async (questionRn, questionData, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const {careview} = await backend.client.careview.updateQuestion(clientRn, questionRn, questionData);

            setClientCareviewSettings(careview);

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_CAREVIEW_UPDATE_QUESTION',
        });
      },
      deleteQuestion: async (questionRn, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const {careview} = await backend.client.careview.deleteQuestion(clientRn, questionRn);

            setClientCareviewSettings(careview);

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_CAREVIEW_DELETE_QUESTION',
        });
      },
      resetQuestions: async (onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const {careview} = await backend.client.careview.resetQuestions(clientRn);

            setClientCareviewSettings(careview);

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_CAREVIEW_RESET_QUESTION',
        });
      },
      reorderQuestions: async (reorderList, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const {careview} = await backend.client.careview.reorderQuestion(clientRn, reorderList);

            setClientCareviewSettings(careview);

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'CLIENT_CAREVIEW_REORDER_QUESTION',
        });
      },
    },
    payment: {
      loadClientHourlyRate: async () => {
        progressWrap(async () => {
          setClientPaymentPlanLoading(true);
          const output = await backend.clientPaymentPlan.loadClientPaymentPlan(clientRn);
          if (output.paymentPlan !== null) {
            setClientPaymentPlan(output.paymentPlan);
          } else {
            setClientPaymentPlan({});
          }
          setClientPaymentPlanLoading(false);
        }, {debugString: 'CLIENT_HOURLY_RATE_LOAD'});
      },

      updateClientHourlyRate: async (hourlyRate, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const output = await backend.clientPaymentPlan.updateClientHourlyRate(clientRn, hourlyRate);
            setClientPaymentPlan(output.paymentPlan);

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_HOURLY_RATE_UPDATE'});
      },

      updateClientStripeId: async (stripeId, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const output = await backend.clientPaymentPlan.updateClientStripeId(clientRn, stripeId);
            setClientPaymentPlan(output.paymentPlan);

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_STRIPE_ID_UPDATE'});
      },

      updateClientAutoCharge: async (autoCharge, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const output = await backend.clientPaymentPlan.updateClientAutoCharge(clientRn, autoCharge);
            setClientPaymentPlan(output.paymentPlan);

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_AUTO_CHARGE_UPDATE'});
      },

      updateClientBillingPeriod: async (billingPeriod, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const output = await backend.clientPaymentPlan.updateClientBillingPeriod(clientRn, billingPeriod);
            setClientPaymentPlan(output.paymentPlan);

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_BILLING_PERIOD_UPDATE'});
      },

      loadPaymentSlips: async () => {
        progressWrap(async () => {
          setClientPaymentSlipLoading(true);
          // Always fetches the latest page fresh (no lastEvaluatedKey fed back in) -- there is no
          // load-more control, so re-fetching on every refresh must replace the list rather than
          // accumulate into it, or the count grows past 5 the longer the page stays open.
          const output = await backend.paymentSlip.queryClientSlip(clientRn);

          setClientPaymentSlipWeekly(output.weekly.list);
          setClientPaymentSlipMonthly(output.monthly.list);
          setClientPaymentSlipLoading(false);
        }, {debugString: 'CLIENT_PAYMENT_SLIP_LOAD'});
      },

      markAsComplete: async (weekOf) => {
        progressWrap(async () => {
          const {slip} = await backend.paymentSlip.markClientSlipCompete(clientRn, weekOf);

          setClientPaymentSlipWeekly((slips) => slips.map((s) => (s.weekOf === weekOf ? slip : s)));
        }, {debugString: 'CLIENT_PAYMENT_SLIP_MARK_COMPLETE'});
      },

      markMonthlyAsComplete: async (monthOf) => {
        progressWrap(async () => {
          const {slip} = await backend.paymentSlip.markClientMonthlySlipCompete(clientRn, monthOf);

          setClientPaymentSlipMonthly((slips) => slips.map((s) => (s.monthOf === monthOf ? slip : s)));
        }, {debugString: 'CLIENT_PAYMENT_SLIP_MARK_MONTHLY_COMPLETE'});
      },

      regenerate: async (date, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const output = await backend.paymentSlip.regenerateClientSlip(clientRn, date);

            if (output.regenerated && output.slip) {
              if (output.monthOf) {
                setClientPaymentSlipMonthly((slips) => {
                  const exists = slips.some((s) => s.monthOf === output.monthOf);
                  return exists ?
                    slips.map((s) => (s.monthOf === output.monthOf ? output.slip : s)) :
                    [output.slip, ...slips];
                });
              } else {
                setClientPaymentSlipWeekly((slips) => {
                  const exists = slips.some((s) => s.weekOf === output.weekOf);
                  return exists ?
                    slips.map((s) => (s.weekOf === output.weekOf ? output.slip : s)) :
                    [output.slip, ...slips];
                });
              }
            }

            onSuccess(output);
          } catch (error) {
            onError(error);
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_PAYMENT_SLIP_REGENERATE'});
      },
    },
    careplan: {
      updateCareplan: async (careplan, progressOptions={}) => {
        return progressWrap(async () => {
          const output = await backend.client.updateCareplan(clientRn, careplan);
          setClientCareplan(output.careplan);
        }, {
          ...progressOptions,
          debugString: 'CLIENT_CAREPLAN_UPDATE',
        });
      },
      updateLeadQuality: async (leadQuality, progressOptions={}) => {
        return progressWrap(async () => {
          const output = await backend.client.updateLeadQuality(clientRn, {leadQuality});
          setClientCareplan(output.careplan);
        }, {
          ...progressOptions,
          debugString: 'CLIENT_CAREPLAN_UPDATE_LEAD_QUALITY',
        });
      },
      getReplacementPreference: async (progressOptions={}) => {
        return progressWrap(async () => {
          const output = await backend.client.getReplacementPreference(clientRn);
          setClientCareplan((careplan) => ({
            ...careplan,
            replacementPreference: output.replacementPreference ?? null,
          }));
          return output.replacementPreference ?? null;
        }, {
          ...progressOptions,
          debugString: 'CLIENT_CAREPLAN_GET_REPLACEMENT_PREFERENCE',
        });
      },
      updateReplacementPreference: async (replacementPreference, progressOptions={}) => {
        return progressWrap(async () => {
          const output = await backend.client.updateReplacementPreference(clientRn, {replacementPreference});
          setClientCareplan(output.careplan);
        }, {
          ...progressOptions,
          debugString: 'CLIENT_CAREPLAN_UPDATE_REPLACEMENT_PREFERENCE',
        });
      },
    },
    user: {
      createUser: async (input) => {
        return progressWrap(async () => {
          setClientAuthenticationLoading(true);
          const result = await backend.user.createClient({
            email: input.email,
            firstName: input.firstName,
            lastName: input.lastName,
            clientRelationshipRn: input.clientRelationshipRn,
          });

          setClientAuthentication((auth) => ({
            ...auth,
            [result.user.userRn]: result.user,
          }));
          setClientAuthenticationLoading(false);
          return result.user;
        }, {debugString: 'CLIENT_CREATE_USER'});
      },

      deactivateUser: async (clientRelationshipRn, onSuccess=()=>undefined) => {
        return progressWrap(async () => {
          setClientAuthenticationLoading(true);
          const result = await backend.user.deactivateUser(clientRelationshipRn);
          setClientAuthentication((auth) => ({
            ...auth,
            [result.user.userRn]: result.user,
          }));
          onSuccess();
          setClientAuthenticationLoading(false);
          return result.user;
        }, {debugString: 'CLIENT_DEACTIVATE_USER'});
      },

      reactivateUser: async (clientRelationshipRn, onSuccess=()=>undefined) => {
        progressWrap(async () => {
          const result = await backend.user.reactivateUser(clientRelationshipRn);
          setClientAuthentication((auth) => {
            return {
              ...auth,
              [clientRelationshipRn]: result.user,
            };
          });
          onSuccess();
          setClientAuthenticationLoading(false);
          return result.user;
        }, {debugString: 'CLIENT_REACTIVATE_USER'});
      },

      getUser: async (userRn, loadAuthData, onSuccess=()=>undefined ) => {
        progressWrap(async () => {
          setClientAuthenticationLoading(true);
          const result = await backend.user.getUser(userRn, loadAuthData);
          setClientAuthentication((auth) => {
            return {
              ...auth,
              [userRn]: result.user,
            };
          });
          onSuccess();
          setClientAuthenticationLoading(false);
          return result.user;
        }, {debugString: 'CLIENT_GET_USER'});
      },
    },
    notification: {
      createUserEmail: async (userRn, email, onComplete=()=>undefined) => {
        progressWrap(async () => {
          try {
            setClientNotificationLoading(true);
            const result = await backend.notification.createUserEmail(userRn, email);
            setClientNotificationChannels((subscribedUsers) => {
              return {
                ...subscribedUsers,
                [result.userRn]: {
                  ...subscribedUsers[result.userRn],
                  emails: [...subscribedUsers[result.userRn].emails, result.userEmailData],
                },
              };
            });
            setClientNotificationLoading(false);
            return result;
          } finally {
            onComplete();
          }
        }, {debugString: 'CREATE_USER_EMAIL'});
      },
      removeUserEmail: async (userRn, email, onComplete=()=>undefined) => {
        progressWrap(async () => {
          try {
            setClientNotificationLoading(true);
            const result = await backend.notification.removeUserEmail(userRn, email);
            setClientNotificationChannels((subscribedUsers) => {
              const updated = {
                ...subscribedUsers,
                [result.userRn]: {
                  ...subscribedUsers[result.userRn],
                  emails: subscribedUsers[result.userRn].emails.filter((emailData) => emailData.email !== email),
                },
              };
              return updated;
            });
            setClientNotificationLoading(false);
            return result;
          } finally {
            onComplete();
          }
        }, {debugString: 'REMOVE_USER_EMAIL'});
      },
      createUserSms: async (userRn, phoneNumber, onComplete=()=>undefined) => {
        progressWrap(async () => {
          try {
            setClientNotificationLoading(true);
            const result = await backend.notification.createUserSms(userRn, phoneNumber);
            setClientNotificationChannels((subscribedUsers) => ({
              ...subscribedUsers,
              [result.userRn]: {
                ...subscribedUsers[result.userRn],
                smsNumbers: [...subscribedUsers[result.userRn].smsNumbers, result.userSmsData],
              },
            }));
            setClientNotificationLoading(false);
            return result;
          } finally {
            onComplete();
          }
        }, {debugString: 'CREATE_USER_SMS'});
      },
      removeUserSms: async (userRn, phoneNumber, onComplete=()=>undefined) => {
        progressWrap(async () => {
          try {
            setClientNotificationLoading(true);
            const result = await backend.notification.removeUserSms(userRn, phoneNumber);
            setClientNotificationChannels((subscribedUsers) => {
              const updated = {
                ...subscribedUsers,
                [result.userRn]: {
                  ...subscribedUsers[result.userRn],
                  smsNumbers: subscribedUsers[result.userRn].smsNumbers.filter((smsData) => smsData.phoneNumber !== phoneNumber),
                },
              };
              return updated;
            });
            setClientNotificationLoading(false);
            return result;
          } finally {
            onComplete();
          }
        }, {debugString: 'REMOVE_USER_SMS'});
      },
      getChannels: async (userRn, onSuccess=()=>undefined) => {
        progressWrap(async () => {
          setClientNotificationLoading(true);
          const result = await backend.notification.getChannels(userRn);
          setClientNotificationChannels((subscribedUsers) => ({
            ...subscribedUsers,
            [result.userRn]: {
              emails: result.userEmails ?? [],
              smsNumbers: result.userSmsNumbers ?? [],
            },
          }));
          onSuccess();
          setClientNotificationLoading(false);
          return result;
        }, {debugString: 'GET_CHANNELS'});
      },
    },
    communication: {
      createSmsSubscription: async (userRn, phoneNumber, onComplete=()=>undefined) => {
        progressWrap(async () => {
          try {
            setClientCommunicationLoading(true);
            const result = await backend.communication.createSmsSubscription(userRn, phoneNumber);
            setClientCommunication((clientCommunication) => ({
              ...clientCommunication,
              [userRn]: result.subscription,
            }));
            setClientCommunicationLoading(false);
            return result;
          } finally {
            onComplete();
          }
        }, {debugString: 'CREATE_COMMUNICATION_SMS_SUBSCRIPTION'});
      },
      getSmsSubscription: async (userRn, onSuccess=()=>undefined) => {
        progressWrap(async () => {
          setClientCommunicationLoading(true);
          const result = await backend.communication.getSmsSubscription(userRn);
          setClientCommunication((clientCommunication) => {
            return {
              ...clientCommunication,
              [userRn]: result.subscription ?? null,
            };
          });
          onSuccess();
          setClientCommunicationLoading(false);
          return result.subscription;
        }, {debugString: 'GET_COMMUNICATION_SMS_SUBSCRIPTION'});
      },
      updateSmsSubscription: async (userRn, phoneNumber, newSubscriptionStatus, onComplete=()=>undefined) => {
        progressWrap(async () => {
          try {
            setClientCommunicationLoading(true);
            const result = await backend.communication.updateSmsSubscription(userRn, phoneNumber, newSubscriptionStatus);
            setClientCommunication((clientCommunication) => ({
              ...clientCommunication,
              [userRn]: result.subscription,
            }));
            setClientCommunicationLoading(false);
            return result.subscription;
          } finally {
            onComplete();
          }
        }, {debugString: 'UPDATE_COMMUNICATION_SMS_SUBSCRIPTION'});
      },
    },
    dialpadIntegration: {
      createDialpadLink: async (clientRelationshipRn, phoneNumber, onComplete=()=>undefined) => {
        progressWrap(async () => {
          try {
            setClientDialpadIntegrationLoading(true);
            const result = await backend.dialpadIntegration.createDialpadLink(clientRelationshipRn, phoneNumber);
            setClientDialpadIntegration((dialpadIntegration) => ({
              ...dialpadIntegration,
              [clientRelationshipRn]: {
                phoneNumber: result.phoneNumber ?? 'CLIENT_ALREADY_REGISTERED',
              },
            }));
            setClientDialpadIntegrationLoading(false);
            return result;
          } finally {
            onComplete();
          }
        }, {debugString: 'CREATE_DIALPAD_INTEGRATION'});
      },
      getDialpadLink: async (userRn, onSuccess=()=>undefined) => {
        progressWrap(async () => {
          setClientDialpadIntegrationLoading(true);
          const result = await backend.dialpadIntegration.getDialpadLink(userRn);
          setClientDialpadIntegration((dialpadIntegration) => {
            // try and find out whose phone number it is
            const matchedRelationshipRn = Object.entries(clientData?.relationships ?? {}).find(([, detail]) => {
              return detail?.contacts?.some((contactData) => (
                contactData?.type === 'Phone' && contactData?.detail === result.phoneNumber
              ));
            })?.[0];
            const isMatchForUser = matchedRelationshipRn === userRn;
            if (matchedRelationshipRn) {
              return {
                ...dialpadIntegration,
                [userRn]: {phoneNumber: isMatchForUser ? result.phoneNumber : 'CLIENT_ALREADY_REGISTERED'},
              };
            } else {
              return {
                ...dialpadIntegration,
                [userRn]: {phoneNumber: null},
              };
            }
          });
          onSuccess();
          setClientDialpadIntegrationLoading(false);
          return result.phoneNumber;
        }, {debugString: 'GET_DIALPAD_INTEGRATION'});
      },
    },
    tasks: {
      addTask: async (taskData, onComplete=()=>undefined) => {
        progressWrap(async () => {
          try {
            const resultResponse = await backend.task.addTask(taskData);
            const result = await hydrateTaskRecord(resultResponse.task);
            const shouldAppend = _.includes(result.connections, clientRn);

            if (shouldAppend) {
              setClientTasks((prev) => _.sortBy(
                  [...prev, result],
                  (task) => moment(task?.dueDate).valueOf(),
              ));
            }
            return result;
          } finally {
            onComplete();
          }
        }, {debugString: 'CREATE_TASK'});
      },
      updateTask: async (taskRn, body, onComplete=()=>undefined) => {
        progressWrap(async () => {
          try {
            const resultResponse = await backend.task.updateTask(taskRn, body);
            const previousTask = clientTasks.find((task) => task.taskRn === taskRn) || null;
            const result = await hydrateTaskRecord(resultResponse.task, previousTask);
            const shouldAppend = result?.connections?.includes(clientRn);
            let nextTasks = clientTasks.map((task) =>
              task.taskRn === taskRn ? result : task,
            );
            if (!shouldAppend) {
              nextTasks = nextTasks.filter((task) => task.taskRn !== taskRn);
            }
            const sortedTasks = _.sortBy(
                nextTasks,
                (task) => new Date(task?.dueDate).getTime() || 0,
            );
            setClientTasks(sortedTasks);
            return result;
          } finally {
            onComplete();
          }
        }, {debugString: 'UPDATE_TASK'});
      },
      removeTask: async (taskRn, onComplete=()=>undefined) => {
        progressWrap(async () => {
          try {
            const result = await backend.task.removeTask(taskRn);
            const nextTasks = _.reject(clientTasks, {taskRn});
            setClientTasks(nextTasks);
            return result;
          } finally {
            onComplete();
          }
        }, {debugString: 'DELETE_TASK'});
      },
      updateTaskStatus: async (taskRn, newStatus, onComplete=()=>undefined) => {
        progressWrap(async () => {
          try {
            setClientTasks((tasks) => {
              const nextTasks = tasks.map((task) => {
                return task.taskRn === taskRn ? {
                  ...task,
                  status: newStatus,
                } : task;
              });
              return _.sortBy(
                  nextTasks,
                  (task) => new Date(task?.dueDate).getTime() || 0,
              );
            });
            const result = await backend.task.updateTaskStatus(taskRn, newStatus);
            return result;
          } finally {
            onComplete();
          }
        }, {debugString: 'UPDATE_TASK_STATUS'});
      },
    },
    playbooks: {
      delete: async (playbookRn, onComplete=()=>undefined) => {
        return progressWrap(async () => {
          try {
            const executionRns = Array.from(new Set(clientTasks.map((task) => {
              const taskExecutionRn = task.executionRn || task.playbookExecutionRn;

              return task.playbookRn === playbookRn ? taskExecutionRn : null;
            }).filter(Boolean)));

            const result = await Promise.all(
                executionRns.map((executionRn) => backend.playbook.execution.delete(playbookRn, executionRn)),
            );

            setClientTasks((tasks) => tasks.filter((task) => task.playbookRn !== playbookRn));

            return result;
          } finally {
            onComplete();
          }
        }, {debugString: 'DELETE_PLAYBOOK'});
      },
      execute: async (playbookRn, resourceRns, onComplete=()=>undefined) => {
        return progressWrap(async () => {
          try {
            const response = await backend.playbook.execution.create(playbookRn, resourceRns);
            const hydratedTasks = await Promise.all((response?.tasks || []).map((task) => hydrateTaskRecord(task)));

            setClientTasks((tasks) => {
              const existingTaskRns = new Set(tasks.map((task) => task.taskRn));
              const nextTasks = hydratedTasks.filter((task) => task?.taskRn && !existingTaskRns.has(task.taskRn));

              return _.sortBy(
                  [...tasks, ...nextTasks],
                  (task) => new Date(task?.dueDate).getTime() || 0,
              );
            });
          } finally {
            onComplete();
          }
        }, {debugString: 'EXECUTE_PLAYBOOK'});
      },
    },
  };

  if (!clientDataLoaded) return null;

  return (
    <Box
      sx={{
        textAlign: 'left',
        width: {
          xs: '100%',
          md: '75%',
        },
      }}
    >
      <ClientFeed
        clientRn={clientRn}
        requesterRn={requesterRn}
        user={requester}
        feedTab={feedTab}
        setFeedTab={setFeedTab}
        clientData={clientData}
        clientMessages={clientMessages}
        clientLogs={clientLogs}
        clientScheduleEvents={clientScheduleEvents}
        clientForms={clientForms}
        clientCareviewSettings={clientCareviewSettings}
        clientBackend={clientBackend}
        refresh={refresh}
        setRefresh={setRefresh}
        showSettings={showSettings}
        clientMessagesLoading={clientMessagesLoading}
        clientMessagesLastEvaluatedKey={clientMessagesLastEvaluatedKey}
        clientLogsLastEvaluatedKey={clientLogsLastEvaluatedKey}
        clientLogsLoading={clientLogsLoading}
        clientPaymentSlipWeekly={clientPaymentSlipWeekly}
        clientPaymentSlipMonthly={clientPaymentSlipMonthly}
        clientPaymentSlipLoading={clientPaymentSlipLoading}
        clientPaymentPlan={clientPaymentPlan}
        clientCareplan={clientCareplan}
        clientPaymentPlanLoading={clientPaymentPlanLoading}
        clientAuthentication={clientAuthentication}
        clientAuthenticationLoading={ClientAuthenticationLoading}
        clientNotificationChannels={clientNotificationChannels}
        clientNotificationLoading={clientNotificationLoading}
        clientCommunication={clientCommunication}
        clientCommunicationLoading={clientCommunicationLoading}
        clientDialpadIntegration={clientDialpadIntegration}
        clientDialpadIntegrationLoading={clientDialpadIntegrationLoading}
        clientTasks={clientTasks}
        clientTasksLoading={clientTasksLoading}
        clientTasksFilterOptions={{clientTasksFilter, setClientTasksFilter}}
        clientCallStatus={clientCallStatus}
        clientOwnerData={clientOwnerData}
      />
    </Box>
  );
};

ClientView.propTypes = {
  clientRn: PropTypes.string.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
  requester: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  requester: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(ClientView));
